import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import {
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Navbar from "../../../../globals/components/Navbar";
import useLawFirmDetails from "./hooks/useLawFirmDetails";
import LawFirmDetailsLoading from "./components/LawFirmDetailsLoading";
import LawFirmDetailsFormCard from "./components/LawFirmDetailsFormCard";
import LawFirmBondHistoryCard from "./components/LawFirmBondHistoryCard";
import LawFirmDetailsTabbedCard from "./components/LawFirmDetailsTabbedCard";
import DeleteLocationDialog from "./components/DeleteLocationDialog";
import DeleteUserDialog from "./components/DeleteUserDialog";
import ReassignPoliciesDialog from "./components/ReassignPoliciesDialog";
import ReassignLogsDialog from "./components/ReassignLogsDialog";

// Register Chart.js components explicitly
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

function LawFirmDetail() {
  const lawFirmDetails = useLawFirmDetails();

  if (lawFirmDetails.loading) {
    return <LawFirmDetailsLoading />;
  }

  const locationsTabProps = {
    id: lawFirmDetails.id,
    navigate: lawFirmDetails.navigate,
    locationSearchQuery: lawFirmDetails.locationSearchQuery,
    handleLocationSearch: lawFirmDetails.handleLocationSearch,
    clearLocationSearch: lawFirmDetails.clearLocationSearch,
    filteredLocations: lawFirmDetails.filteredLocations,
    locations: lawFirmDetails.locations,
    paginatedLocations: lawFirmDetails.paginatedLocations,
    locationsPage: lawFirmDetails.locationsPage,
    locationsRowsPerPage: lawFirmDetails.locationsRowsPerPage,
    handleLocationsPageChange: lawFirmDetails.handleLocationsPageChange,
    handleLocationsRowsPerPageChange:
      lawFirmDetails.handleLocationsRowsPerPageChange,
    handleDeleteLocationClick: lawFirmDetails.handleDeleteLocationClick,
  };

  const usersTabProps = {
    id: lawFirmDetails.id,
    navigate: lawFirmDetails.navigate,
    role: lawFirmDetails.role,
    userSearchQuery: lawFirmDetails.userSearchQuery,
    handleUserSearch: lawFirmDetails.handleUserSearch,
    clearUserSearch: lawFirmDetails.clearUserSearch,
    filteredTeamMembers: lawFirmDetails.filteredTeamMembers,
    teamMembers: lawFirmDetails.teamMembers,
    paginatedTeamMembers: lawFirmDetails.paginatedTeamMembers,
    teamMembersPage: lawFirmDetails.teamMembersPage,
    teamMembersRowsPerPage: lawFirmDetails.teamMembersRowsPerPage,
    handleTeamMembersPageChange: lawFirmDetails.handleTeamMembersPageChange,
    handleTeamMembersRowsPerPageChange:
      lawFirmDetails.handleTeamMembersRowsPerPageChange,
    handleToggle: lawFirmDetails.handleToggle,
    setTeamMembers: lawFirmDetails.setTeamMembers,
    handleOpenReassignLogs: lawFirmDetails.handleOpenReassignLogs,
    openReassignDialog: lawFirmDetails.openReassignDialog,
    handleDeleteUserClick: lawFirmDetails.handleDeleteUserClick,
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Grid container spacing={3}>
          <LawFirmDetailsFormCard
            lawFirmData={lawFirmDetails.lawFirmData}
            setLawFirmData={lawFirmDetails.setLawFirmData}
            role={lawFirmDetails.role}
            internalUsers={lawFirmDetails.internalUsers}
            isEditing={lawFirmDetails.isEditing}
            isSaving={lawFirmDetails.isSaving}
            handleViewLawFirmBonds={lawFirmDetails.handleViewLawFirmBonds}
            handleEditClick={lawFirmDetails.handleEditClick}
            handleCancelClick={lawFirmDetails.handleCancelClick}
            handleSave={lawFirmDetails.handleSave}
            formatDateForInput={lawFirmDetails.formatDateForInput}
          />

          {(lawFirmDetails.role === "super_user" ||
            lawFirmDetails.role === "internal_user") && (
            <LawFirmBondHistoryCard graphData={lawFirmDetails.graphData} />
          )}

          <LawFirmDetailsTabbedCard
            tabValue={lawFirmDetails.tabValue}
            handleTabChange={lawFirmDetails.handleTabChange}
            filteredLocations={lawFirmDetails.filteredLocations}
            locations={lawFirmDetails.locations}
            locationSearchQuery={lawFirmDetails.locationSearchQuery}
            filteredTeamMembers={lawFirmDetails.filteredTeamMembers}
            teamMembers={lawFirmDetails.teamMembers}
            userSearchQuery={lawFirmDetails.userSearchQuery}
            locationsTabProps={locationsTabProps}
            usersTabProps={usersTabProps}
          />
        </Grid>
      </Container>

      <DeleteLocationDialog
        deleteLocationDialogOpen={lawFirmDetails.deleteLocationDialogOpen}
        setDeleteLocationDialogOpen={lawFirmDetails.setDeleteLocationDialogOpen}
        selectedLocation={lawFirmDetails.selectedLocation}
        deleteLocationLoading={lawFirmDetails.deleteLocationLoading}
        handleConfirmDeleteLocation={lawFirmDetails.handleConfirmDeleteLocation}
      />

      <DeleteUserDialog
        deleteUserDialogOpen={lawFirmDetails.deleteUserDialogOpen}
        setDeleteUserDialogOpen={lawFirmDetails.setDeleteUserDialogOpen}
        selectedUser={lawFirmDetails.selectedUser}
        deleteUserLoading={lawFirmDetails.deleteUserLoading}
        handleConfirmDeleteUser={lawFirmDetails.handleConfirmDeleteUser}
      />

      <ReassignPoliciesDialog
        reassignDialogOpen={lawFirmDetails.reassignDialogOpen}
        setReassignDialogOpen={lawFirmDetails.setReassignDialogOpen}
        reassignFromMember={lawFirmDetails.reassignFromMember}
        replacementUsers={lawFirmDetails.replacementUsers}
        selectedReplacementUser={lawFirmDetails.selectedReplacementUser}
        setSelectedReplacementUser={lawFirmDetails.setSelectedReplacementUser}
        selectedBondOptions={lawFirmDetails.selectedBondOptions}
        setSelectedBondOptions={lawFirmDetails.setSelectedBondOptions}
        availableBonds={lawFirmDetails.availableBonds}
        bondsLoading={lawFirmDetails.bondsLoading}
        bondsError={lawFirmDetails.bondsError}
        reassignSubmitting={lawFirmDetails.reassignSubmitting}
        handleSubmitReassign={lawFirmDetails.handleSubmitReassign}
      />

      <ReassignLogsDialog
        reassignLogsDialogOpen={lawFirmDetails.reassignLogsDialogOpen}
        setReassignLogsDialogOpen={lawFirmDetails.setReassignLogsDialogOpen}
        reassignLogsLoading={lawFirmDetails.reassignLogsLoading}
        reassignLogsError={lawFirmDetails.reassignLogsError}
        reassignLogs={lawFirmDetails.reassignLogs}
        formatDateTime={lawFirmDetails.formatDateTime}
        renderReassignComment={lawFirmDetails.renderReassignComment}
      />
    </>
  );
}

export default LawFirmDetail;
